import { ChangeEvent, HTMLAttributes, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Button,
  Chip,
  LinearProgress,
  Link,
  makeStyles,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { Alert } from "@material-ui/lab";
import ButtonWithLoading from "components/ButtonWithLoading";
import VisibilityButton from "components/VisibilityButton";
import {
  getErrorCode,
  getErrorDescription,
} from "utils/apiRequestWithErrorCode";
import { removeAISettings, saveAISettings, testAISettings } from "../../api";
import AdvancedSettings from "./AdvancedSettings";
import DisconnectDialog from "./DisconnectDialog";
import ModelField from "./ModelField";
import ProviderAvatar from "../ProviderAvatar";
import TestResult from "./TestResult";
import TestProgress from "./TestResult/TestProgress";
import {
  emptyForm,
  FormState,
  getFormErrors,
  getStoredSecret,
  isComplete,
  isSameForm,
  Outcome,
  toForm,
  toSettings,
} from "./helpers";

import { AIProvider, AISettings } from "@dashboard-v3/api";

type Busy = "test" | "save" | "disconnect" | null;

interface Props {
  providers: AIProvider[];
  settings: AISettings | null;
  initialProviderId: string;
  onSaved: () => Promise<unknown>;
  onDisconnected: () => void;
  onCancel: () => void;
}

export default function ProviderForm(props: Props) {
  const { providers, settings, initialProviderId } = props;
  const { onSaved, onDisconnected, onCancel } = props;
  const { t } = useTranslation("storage");
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const savedForm = settings ? toForm(settings) : null;
  const [form, setForm] = useState<FormState>(
    () => savedForm || emptyForm(findProvider(providers, initialProviderId))
  );
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState<Busy>(null);
  const [outcome, setOutcome] = useState<Outcome>(null);
  const [confirmsDisconnect, setConfirmsDisconnect] = useState(false);

  const provider = findProvider(providers, form.provider);
  const errors = getFormErrors(form);
  const isBusy = busy !== null;
  const isConnected = savedForm?.provider === form.provider;
  const isDirty = !isSameForm(form, savedForm);
  const storedSecret = isConnected
    ? getStoredSecret(settings, form.provider)
    : "";
  const hasStoredSecret = Boolean(storedSecret);
  const isValid =
    isComplete(form, provider, hasStoredSecret) &&
    Object.keys(errors).length === 0;
  const canTest = isValid && !isBusy;
  const canSave = isValid && isDirty && !isBusy;
  const runningMode = busy === "disconnect" ? null : busy;
  const hasSaved = Boolean(savedForm);
  const savedProviderName = hasSaved
    ? findProvider(providers, savedForm.provider).displayName
    : "";
  const replacesProvider = hasSaved && !isConnected;
  const showUnsaved = hasSaved && isDirty && !isBusy;
  const keyType = showKey ? "text" : "password";
  const keyHelper = hasStoredSecret
    ? t("externalAI.form.apiKeyKeepHelper")
    : "";
  const keyLabelProps = hasStoredSecret ? { shrink: true } : undefined;
  const keyAdornment = form.secret ? (
    <VisibilityButton
      isVisible={showKey}
      onClick={() => setShowKey(prev => !prev)}
    />
  ) : undefined;
  const statusLabel = isConnected
    ? t("externalAI.status.connected")
    : t("externalAI.status.notConnected");
  const statusClass = isConnected ? classes.connected : classes.notConnected;
  const subtitle = isConnected
    ? t("externalAI.form.connectedModel", {
        model: savedForm.model,
        interpolation: { escapeValue: false },
      })
    : t("externalAI.form.notConnectedHint");

  function updateForm(update: Partial<FormState>) {
    setForm(prev => ({ ...prev, ...update }));
  }

  function handleModelChange(model: string) {
    const isNewModel = model.trim() !== form.model.trim();
    const selected = provider.models.find(item => item.id === model.trim());
    const clearsTemperature = isNewModel && selected?.temperature === false;

    if (clearsTemperature) {
      updateForm({ model, temperature: "" });
      return;
    }

    updateForm({ model });
  }

  function handleProviderChange(e: ChangeEvent<HTMLInputElement>) {
    const providerId = e.target.value;
    const isSavedProvider = savedForm?.provider === providerId;
    const nextForm = isSavedProvider
      ? savedForm
      : emptyForm(findProvider(providers, providerId));

    setForm(nextForm);
    setOutcome(null);
    setShowKey(false);
  }

  function handleDiscard() {
    setForm(savedForm);
    setOutcome(null);
  }

  async function handleTest() {
    setBusy("test");
    setOutcome(null);
    try {
      const result = await testAISettings(toSettings(form, provider));
      setOutcome({
        mode: "test",
        ok: result.ok,
        code: result.code,
        probe: result.probe,
      });
    } catch (error) {
      setOutcome({ mode: "test", ok: false, code: getErrorCode(error) });
    } finally {
      setBusy(null);
    }
  }

  async function handleSave() {
    const newSettings = toSettings(form, provider);
    setBusy("save");
    setOutcome(null);
    try {
      const result = await saveAISettings(newSettings);
      setForm(toForm(newSettings));
      setShowKey(false);
      await onSaved().catch(() => undefined);
      setOutcome({ mode: "save", ok: true, probe: result.probe });
    } catch (error) {
      setOutcome({
        mode: "save",
        ok: false,
        code: getErrorCode(error),
        probe: getProbeFromError(error),
      });
    } finally {
      setBusy(null);
    }
  }

  async function handleDisconnect() {
    setConfirmsDisconnect(false);
    setBusy("disconnect");
    try {
      await removeAISettings();
      enqueueSnackbar(t("externalAI.disconnected"), { variant: "success" });
      onDisconnected();
    } catch (error) {
      setBusy(null);
      enqueueSnackbar(t("externalAI.error.disconnect"), { variant: "error" });
    }
  }

  return (
    <Paper className={classes.card}>
      {isBusy && <LinearProgress className={classes.progress} />}
      <div className={classes.header}>
        <ProviderAvatar provider={provider} />
        <div>
          <div className={classes.title}>
            <Typography variant="subtitle1" className={classes.name}>
              {provider.displayName}
            </Typography>
            <Chip
              size="small"
              label={
                <span className={classes.statusLabel}>
                  <span className={classes.statusDot} />
                  {statusLabel}
                </span>
              }
              className={statusClass}
              data-testid="external-ai__status"
            />
          </div>
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
        </div>
        <div className={classes.headerActions}>
          {hasSaved && (
            <>
              <ButtonWithLoading
                color="secondary"
                loading={busy === "disconnect"}
                disabled={isBusy}
                onClick={() => setConfirmsDisconnect(true)}
                data-testid="external-ai__disconnect"
              >
                {t("externalAI.disconnect.button")}
              </ButtonWithLoading>
              <DisconnectDialog
                open={confirmsDisconnect}
                providerName={savedProviderName}
                onCancel={() => setConfirmsDisconnect(false)}
                onConfirm={handleDisconnect}
              />
            </>
          )}
          {!hasSaved && (
            <Button color="primary" disabled={isBusy} onClick={onCancel}>
              {t("common:cancel")}
            </Button>
          )}
        </div>
      </div>

      <div className={classes.body}>
        {replacesProvider && (
          <Alert severity="warning" className={classes.replaceNote}>
            {t("externalAI.form.replaceWarning", {
              current: findProvider(providers, savedForm.provider).displayName,
              next: provider.displayName,
            })}
          </Alert>
        )}
        <div className={classes.connection}>
          <TextField
            select
            label={t("externalAI.form.provider")}
            variant="outlined"
            fullWidth
            disabled={isBusy}
            value={form.provider}
            onChange={handleProviderChange}
            SelectProps={{
              classes: { select: classes.providerSelect },
              SelectDisplayProps: {
                "data-testid": "external-ai__provider-select",
              } as HTMLAttributes<HTMLDivElement>,
            }}
          >
            {providers.map(option => (
              <MenuItem key={option.id} value={option.id}>
                <span className={classes.providerOption}>
                  <ProviderAvatar provider={option} small />
                  {option.displayName}
                </span>
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t("externalAI.form.apiKey")}
            type={keyType}
            variant="outlined"
            fullWidth
            required={!hasStoredSecret}
            disabled={isBusy}
            value={form.secret}
            placeholder={storedSecret}
            helperText={keyHelper}
            autoComplete="new-password"
            onChange={e => updateForm({ secret: e.target.value })}
            inputProps={{
              spellCheck: false,
              "data-testid": "external-ai__api-key",
            }}
            InputLabelProps={keyLabelProps}
            InputProps={{ endAdornment: keyAdornment }}
          />
        </div>
        {provider.baseUrlRequired && (
          <TextField
            label={t("externalAI.form.baseUrl")}
            variant="outlined"
            fullWidth
            required
            disabled={isBusy}
            value={form.baseUrl}
            helperText={t("externalAI.form.baseUrlHelper")}
            className={classes.field}
            onChange={e => updateForm({ baseUrl: e.target.value })}
            inputProps={{
              spellCheck: false,
              "data-testid": "external-ai__base-url",
            }}
          />
        )}
        <div className={classes.field}>
          <ModelField
            provider={provider}
            value={form.model}
            disabled={isBusy}
            onChange={handleModelChange}
          />
        </div>
        <AdvancedSettings
          form={form}
          provider={provider}
          errors={errors}
          disabled={isBusy}
          onChange={updateForm}
        />
        {runningMode && <TestProgress mode={runningMode} />}
        {outcome && (
          <TestResult
            outcome={outcome}
            providerName={provider.displayName}
            onClose={() => setOutcome(null)}
          />
        )}
      </div>

      <div className={classes.footer}>
        <Button
          color="primary"
          variant="outlined"
          disabled={!canTest}
          onClick={handleTest}
          data-testid="external-ai__test"
        >
          {t("externalAI.form.test")}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disableElevation
          disabled={!canSave}
          onClick={handleSave}
          data-testid="external-ai__save"
        >
          {t("externalAI.form.save")}
        </Button>
        {showUnsaved && (
          <div className={classes.unsavedState}>
            <span className={classes.footerSeparator} />
            <span
              className={classes.unsaved}
              data-testid="external-ai__unsaved"
            >
              <span className={classes.unsavedDot} />
              {t("externalAI.form.unsaved")}
            </span>
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              className={classes.discard}
              onClick={handleDiscard}
              data-testid="external-ai__discard"
            >
              {t("externalAI.form.discard")}
            </Link>
          </div>
        )}
        <Typography
          variant="caption"
          color="textSecondary"
          className={classes.footerNote}
        >
          {t("externalAI.form.saveNote")}
        </Typography>
      </div>
    </Paper>
  );
}

function findProvider(providers: AIProvider[], providerId: string) {
  return providers.find(item => item.id === providerId) || providers[0];
}

function getProbeFromError(error: unknown): Outcome["probe"] {
  const description = getErrorDescription(error);
  if (!description || typeof description !== "object") return undefined;

  return description as Outcome["probe"];
}

const useStyles = makeStyles(theme => ({
  card: { position: "relative" },
  progress: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    borderRadius: "4px 4px 0 0",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "16px 20px",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  title: { display: "flex", alignItems: "center", gap: 10 },
  name: { fontWeight: 500 },
  connected: {
    color: green[700],
    backgroundColor: green[50],
    fontWeight: 500,
  },
  notConnected: {
    color: theme.palette.text.secondary,
    backgroundColor: "rgba(0, 0, 0, 0.06)",
    fontWeight: 500,
  },
  statusLabel: { display: "inline-flex", alignItems: "center", gap: 6 },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    backgroundColor: "currentColor",
  },
  headerActions: { marginLeft: "auto" },
  body: { padding: 20, maxWidth: 980 },
  replaceNote: { marginBottom: 20 },
  connection: {
    display: "grid",
    gridTemplateColumns: "minmax(200px, 1fr) minmax(260px, 2fr)",
    gap: 16,
  },
  providerSelect: { paddingTop: 16, paddingBottom: 16 },
  providerOption: { display: "flex", alignItems: "center", gap: 10 },
  field: { marginTop: 20 },
  footer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    padding: "14px 20px",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  unsavedState: { display: "flex", alignItems: "center", gap: 12 },
  footerSeparator: {
    width: 1,
    height: 24,
    margin: "0 4px",
    backgroundColor: theme.palette.divider,
  },
  unsaved: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 10px",
    borderRadius: 12,
    backgroundColor: "#fff4e0",
    color: "#8a5300",
    fontSize: 12,
    fontWeight: 500,
  },
  unsavedDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    backgroundColor: "#f0a020",
  },
  discard: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    "&:hover": { color: theme.palette.text.primary },
  },
  footerNote: { marginLeft: "auto", textAlign: "right" },
}));
